import {
  createContext, type ReactNode, type RefObject, useContext, useEffect, useMemo, useRef,
} from 'react';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';

const TooltipContext = createContext<{
  isMobileScreen: boolean;
  lastFocusedElementRef: RefObject<HTMLElement | null>;
}>({
  isMobileScreen: false,
  lastFocusedElementRef: { current: null },
});

export const useTooltipContext = () => useContext(TooltipContext);

export const TooltipContextProvider = ({ children }: { children: ReactNode }) => {
  const isMobileScreen = useIsMobileScreenSelector();
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (document?.body) { // Only in a browser.
      const focusHandler = (event: FocusEvent) => {
        lastFocusedElementRef.current = event.target as HTMLElement;
      };

      document.body.addEventListener('focusin', focusHandler);
      return () => {
        document.body.removeEventListener('focusin', focusHandler);
      };
    }
    return undefined;
  }, []);

  const value = useMemo(() => ({
    lastFocusedElementRef,
    isMobileScreen,
  }), [isMobileScreen]);

  return (
    <TooltipContext.Provider value={value}>
      {children}
    </TooltipContext.Provider>
  );
};
